// Per-device display preferences, kept in local storage.
const STORAGE_KEY = "preferences";

export const THEMES = ["auto", "light", "dark"];

/** Lyric size steps on the live page, in pixels: small, comfortable, large, arm's length. */
export const LYRIC_SIZES = [22, 26, 30, 36];
export const LYRIC_SIZE_LABELS = ["Small", "Comfortable", "Large", "Arm’s length"];

/** Transposition limits, in semitones. */
export const MIN_SEMITONES = -11;
export const MAX_SEMITONES = 11;

/** How many songs a device remembers a key for; the oldest entry goes first. */
export const MAX_SONG_KEYS = 200;

export const defaultPreferences = Object.freeze({
  lyricSize: 1, // index into LYRIC_SIZES
  theme: "auto", // "auto" follows the system setting; "dark" is the dim room
  highContrast: false, // pure black (or white) text at a heavier weight
  showChords: false, // chords above the lines that have them (off everywhere by default)
  keepAwake: true, // keep the screen on while lyrics are shown
  keepFavoritesOffline: true, // keep a copy of the favourites on the device
  songKeys: {}, // songId -> semitones this device transposes the song by
});

export const clampLyricSize = (value) => {
  const index = Math.round(Number(value));
  if (!Number.isFinite(index)) return defaultPreferences.lyricSize;
  return Math.min(LYRIC_SIZES.length - 1, Math.max(0, index));
};

export const clampSemitones = (value) => {
  const semitones = Math.round(Number(value));
  if (!Number.isFinite(semitones)) return 0;
  return Math.min(MAX_SEMITONES, Math.max(MIN_SEMITONES, semitones));
};

// Devices that stored the older continuous scale (0.7 to 1.8) get the nearest step.
const sizeFromScale = (scale) => {
  const value = Number(scale);
  if (!Number.isFinite(value)) return defaultPreferences.lyricSize;
  if (value < 0.9) return 0;
  if (value < 1.15) return 1;
  if (value < 1.45) return 2;
  return 3;
};

const cleanSongKeys = (value) => {
  if (!value || typeof value !== "object") return {};
  const keys = {};
  for (const [songId, semitones] of Object.entries(value)) {
    const id = Number(songId);
    const offset = clampSemitones(semitones);
    if (Number.isInteger(id) && id > 0 && offset !== 0) keys[id] = offset;
  }
  return keys;
};

/** Loads stored preferences, ignoring anything unknown or malformed. */
export function loadPreferences() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
    const boolean = (key) => (typeof stored[key] === "boolean" ? stored[key] : defaultPreferences[key]);
    return {
      lyricSize: stored.lyricSize !== undefined ? clampLyricSize(stored.lyricSize) : sizeFromScale(stored.lyricsScale ?? 1),
      theme: THEMES.includes(stored.theme) ? stored.theme : defaultPreferences.theme,
      highContrast: boolean("highContrast"),
      showChords: boolean("showChords"),
      keepAwake: boolean("keepAwake"),
      keepFavoritesOffline: boolean("keepFavoritesOffline"),
      songKeys: cleanSongKeys(stored.songKeys),
    };
  } catch {
    return { ...defaultPreferences, songKeys: {} };
  }
}

export function savePreferences(preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // storage unavailable: preferences last for this page load only
  }
}

/** The stored keys with one song's key changed (0 forgets it); the oldest entries make room. */
export function withSongKey(songKeys, songId, semitones) {
  const { [songId]: previous, ...rest } = songKeys || {};
  void previous;
  const offset = clampSemitones(semitones);
  if (offset === 0) return rest;
  const entries = Object.entries(rest);
  const kept = entries.length >= MAX_SONG_KEYS ? entries.slice(entries.length - MAX_SONG_KEYS + 1) : entries;
  return { ...Object.fromEntries(kept), [songId]: offset };
}

/** Whether the device shows the dark theme for the given preference and system setting. */
export const isDarkTheme = (theme) => {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
};
